import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import {
    getPublicationActPackagesGetDownloadActPackageQueryKey,
    getPublicationActPackagesGetListActPackagesQueryKey,
    getPublicationActReportsGetDownloadActPackageReportQueryKey,
    getPublicationAnnouncementPackagesGetDownloadAnnouncementPackageQueryKey,
    getPublicationAnnouncementPackagesGetListAnnouncementPackagesQueryKey,
    getPublicationAnnouncementReportsGetDownloadAnnouncementPackageReportQueryKey,
    getPublicationEnvironmentsGetDetailEnvironmentQueryKey,
    getPublicationPackagesGetListUnifiedPackagesQueryKey,
    getPublicationVersionsGetListVersionsQueryKey,
    usePublicationActPackagesPostCreateActPackage,
    usePublicationActReportsPostUploadActPackageReport,
    usePublicationAnnouncementPackagesPostCreateAnnouncementPackage,
    usePublicationAnnouncementReportsPostUploadAnnouncementPackageReport,
} from '@/api/fetchers'
import { HTTPValidationError, PackageType } from '@/api/fetchers.schemas'
import { downloadFile } from '@/utils/file'

import useModalStore from '@/store/modalStore'
import { PublicationType } from '../../types'

interface ActionsProps {
    publicationType: PublicationType
    packageType?: PackageType
    versionUUID?: string
    announcementUUID?: string
    publicationUUID: string
    environmentUUID?: string
    packageUUID?: string
    reportUUID?: string
}

export const useActions = ({
    publicationType,
    packageType,
    versionUUID,
    announcementUUID,
    publicationUUID,
    environmentUUID,
    packageUUID,
    reportUUID,
}: ActionsProps) => {
    const queryClient = useQueryClient()
    const setActiveModal = useModalStore(state => state.setActiveModal)

    const createPackage = (
        publicationType === 'act'
            ? usePublicationActPackagesPostCreateActPackage
            : usePublicationAnnouncementPackagesPostCreateAnnouncementPackage
    )({
        mutation: {
            onSuccess: () => {
                if (publicationType === 'act') {
                    queryClient.invalidateQueries({
                        queryKey:
                            getPublicationActPackagesGetListActPackagesQueryKey(
                                {
                                    version_uuid: versionUUID,
                                }
                            ),
                    })
                } else {
                    queryClient.invalidateQueries({
                        queryKey:
                            getPublicationAnnouncementPackagesGetListAnnouncementPackagesQueryKey(
                                {
                                    announcement_uuid: announcementUUID,
                                }
                            ),
                    })
                }
                queryClient.invalidateQueries({
                    queryKey:
                        getPublicationVersionsGetListVersionsQueryKey(
                            publicationUUID
                        ),
                })
                queryClient.invalidateQueries({
                    queryKey: getPublicationVersionsGetListVersionsQueryKey(
                        publicationUUID,
                        {
                            limit: 100,
                        }
                    ),
                })
                queryClient.invalidateQueries({
                    queryKey:
                        getPublicationPackagesGetListUnifiedPackagesQueryKey(),
                    refetchType: 'all',
                    exact: false,
                })

                if (packageType === 'publication' && environmentUUID) {
                    queryClient.invalidateQueries({
                        queryKey:
                            getPublicationEnvironmentsGetDetailEnvironmentQueryKey(
                                environmentUUID
                            ),
                    })
                }
            },
            onError: (error: AxiosError<HTTPValidationError>) => {
                if (
                    Array.isArray(error.response?.data.detail) &&
                    error.status === 444
                ) {
                    setActiveModal('publicationScan', {
                        errors: error.response?.data.detail as any[],
                    })
                }
                console.error(
                    'Er is iets mis gegaan bij het maken van de levering, zie foutmelding:\n\n',
                    JSON.stringify(error.response?.data.detail, null, 2)
                )
            },
        },
    })

    const downloadPackage = useQuery({
        queryKey: ['downloadPackage', packageUUID, versionUUID],
        queryFn: async () =>
            downloadFile(
                (publicationType === 'act'
                    ? getPublicationActPackagesGetDownloadActPackageQueryKey
                    : getPublicationAnnouncementPackagesGetDownloadAnnouncementPackageQueryKey)(
                    String(packageUUID)
                )[0]
            ).finally(() =>
                queryClient.invalidateQueries({
                    queryKey:
                        publicationType === 'act'
                            ? getPublicationActPackagesGetListActPackagesQueryKey(
                                  {
                                      version_uuid: versionUUID,
                                  }
                              )
                            : getPublicationAnnouncementPackagesGetListAnnouncementPackagesQueryKey(
                                  {
                                      announcement_uuid: announcementUUID,
                                  }
                              ),
                })
            ),
        enabled: false,
    })

    const downloadReport = useQuery({
        queryKey: ['downloadReport', reportUUID, versionUUID],
        queryFn: async () =>
            downloadFile(
                (publicationType === 'act'
                    ? getPublicationActReportsGetDownloadActPackageReportQueryKey
                    : getPublicationAnnouncementReportsGetDownloadAnnouncementPackageReportQueryKey)(
                    String(reportUUID)
                )[0]
            ),
        enabled: false,
    })

    const uploadReports = (
        publicationType === 'act'
            ? usePublicationActReportsPostUploadActPackageReport
            : usePublicationAnnouncementReportsPostUploadAnnouncementPackageReport
    )({
        mutation: {
            onSuccess: data => {
                if (publicationType === 'act') {
                    queryClient.invalidateQueries({
                        queryKey:
                            getPublicationActPackagesGetListActPackagesQueryKey(
                                {
                                    version_uuid: versionUUID,
                                }
                            ),
                    })
                } else {
                    queryClient.invalidateQueries({
                        queryKey:
                            getPublicationAnnouncementPackagesGetListAnnouncementPackagesQueryKey(
                                {
                                    announcement_uuid: announcementUUID,
                                }
                            ),
                    })
                }

                if (data.Status === 'valid') {
                    queryClient.invalidateQueries({
                        queryKey:
                            getPublicationVersionsGetListVersionsQueryKey(
                                publicationUUID
                            ),
                    })
                }

                queryClient.invalidateQueries({
                    queryKey:
                        getPublicationPackagesGetListUnifiedPackagesQueryKey(),
                    refetchType: 'all',
                    exact: false,
                })

                if (packageType === 'publication' && environmentUUID) {
                    queryClient.invalidateQueries({
                        queryKey:
                            getPublicationEnvironmentsGetDetailEnvironmentQueryKey(
                                environmentUUID
                            ),
                    })
                }
            },
        },
    })

    return { createPackage, downloadPackage, downloadReport, uploadReports }
}
